import dayjs from "dayjs";
import moment from "moment";
import { MutableRefObject } from "react";

export class MethodsUtils {
  static ConvertToDate(
    dateConvert: Date | string,
    defaultBrasil?: boolean
  ): Date | string {
    let parseDate = moment(dateConvert, ["DD/MM/YYYY", "YYYY-MM-DD"]);

    if (!parseDate.isValid()) {
      throw new Error("Data inválida");
    }

    if (defaultBrasil) {
      return parseDate.format("DD/MM/YYYY");
    }
    return parseDate.format("YYYY-MM-DD");
  }

  static FormatCpfCnpj(input: string): string | null {
    if (!input) {
      return null;
    }

    const sanitizedInput: string = input.replace(/\D/g, "");

    if (/^\d{11}$/.test(sanitizedInput)) {
      return sanitizedInput.replace(
        /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
        "$1.$2.$3-$4"
      );
    }

    if (/^\d{14}$/.test(sanitizedInput)) {
      return sanitizedInput.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        "$1.$2.$3/$4-$5"
      );
    }
    return null;
  }

  static async convertTimeToMilliseconds(
    timeInput: Date | string,
    includeSeconds: boolean = true
  ): Promise<number> {
    let time;

    if (timeInput instanceof Date) {
      time = moment(timeInput).startOf("day");
    } else {
      if (timeInput.length === 4 && !timeInput.includes(":")) {
        timeInput = timeInput.slice(0, 2) + ":" + timeInput.slice(2);
      }

      const [hoursStr, minutesStr, secondsStr] = timeInput.split(":");
      const hours = parseInt(hoursStr, 10);
      const minutes = parseInt(minutesStr, 10);
      const seconds = parseInt(secondsStr || "0", 10);

      return (
        hours * 3600000 +
        minutes * 60000 +
        (includeSeconds ? seconds * 1000 : 0)
      );
    }

    const milliseconds =
      time.hours() * 3600000 +
      time.minutes() * 60000 +
      (includeSeconds ? time.seconds() * 1000 : 0);

    return milliseconds;
  }

  static async convertMillisecondsToTime(milliseconds: number, includeSeconds: boolean): Promise<string> {
    const hours = Math.floor(milliseconds / 3600000);
    const minutes = Math.floor((milliseconds % 3600000) / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);

    if (includeSeconds)
      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    else
      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
  }

  static async diffHour(hourEnd: string, hourStart: string): Promise<number> {
    const hourFinally: number = await this.convertTimeToMilliseconds(hourEnd);
    const hourInit: number = await this.convertTimeToMilliseconds(hourStart);
    const result: number = hourFinally - hourInit;

    return result < 0 ? 0 : result;
  }

  static async intervalCalculated(hourEnd: string, hourStart: string, interval: string): Promise<string> {
    const diff: number = await this.diffHour(hourEnd, hourStart);
    const convInterval: number = await this.convertTimeToMilliseconds(interval, false);
    const result: number = diff - convInterval;

    return result > 0
      ? await this.convertMillisecondsToTime(result, false)
      : await this.convertMillisecondsToTime(0, false);
  }

  static async isValidHour(timeString: string): Promise<boolean> {
    const timePattern = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/;

    if (!timePattern.test(timeString)) {
      return false;
    }

    const [hours, minutes] = timeString.split(":").map(Number);
    return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
  }

  static removeCharString(
    value: string,
    charRemove: string,
    charSub: string
  ): string {
    return value.replace(charRemove, charSub);
  }


  static formatName(fullName: string): string {
    const names = fullName.trim().split(/\s+/);

    if (names.length === 1) {
      return names[0];
    }

    return `${names[0]} ${names[names.length - 1]}`;
  }

  static async formatDocument(document: string): Promise<string> {
    document = document.replace(/\D/g, ""); // Remove qualquer caractere não numérico
    document = document.slice(0, 11);

    if (document.length <= 3) {
      return document;
    } else if (document.length <= 6) {
      return document.replace(/(\d{3})(\d{0,3})/, "$1.$2");
    } else if (document.length <= 9) {
      return document.replace(/(\d{3})(\d{3})(\d{0,3})/, "$1.$2.$3");
    } else {
      return document.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, "$1.$2.$3-$4");
    }

  }

  static async generateDateHourGMTBrasilDF(): Promise<string> {
    return dayjs(new Date()).subtract(3, "hour").toString();
  }

  static async generateDateHour(): Promise<string> {
    return dayjs(new Date()).toString();
  }

  static extractNumberFromString(input: string): number {
    const numberString = input.replace(/\D/g, "");
    return parseInt(numberString, 10);
  }

  static unformatedCpf(input: string): string {
    return input.replace(/\D/g, "");
  }

  static async loadPermissionParticularitie(crud: string) {
    const rolesParticularities = localStorage.getItem('rolesUser');
    if (rolesParticularities) {
      const roles = JSON.parse(rolesParticularities);

      const permissionsValues = roles
        .filter((perm: { role_permission_value: string }) => perm.role_permission_value.includes("particularities"))
        .map((perm: { role_permission_value: string }) => perm.role_permission_value);

      const particularitieAdd = permissionsValues.some((value: string) => value === "particularities-add" || value === "particularities");
      const particularitieEdit = permissionsValues.some((value: string) => value === "particularities-edit" || value === "particularities");
      const particularitieView = permissionsValues.some((value: string | string[]) => value === "particularities-view" || value === "particularities")
      const exceptionParticularitie = crud === 'clients' && (particularitieAdd || particularitieEdit);

      return {
        particularitieAdd: particularitieAdd,
        particularitieEdit: particularitieEdit,
        particularitieView: particularitieView,
        exceptionParticularitie: exceptionParticularitie
      };
    } else {

      return {
        particularitieAdd: false,
        particularitieEdit: false,
        particularitieView: false,
        exceptionParticularitie: false
      };
    }
  }

  static async formatTimeStampToYearMonthDay(value: Date): Promise<string> {
    return dayjs(value).format('YYYY-MM-DD');
  }

  static formatDateToBR(date: Date, includeHours?: boolean): string {

    if (includeHours) {
      return dayjs(date).format('DD/MM/YYYY - HH:mm:ss');
    }

    return dayjs(date).format('DD/MM/YYYY');
  }

  static getRandomColor = () => {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }

    return color;

  };

  static getStaticColor = (id: number) => {
    const colors = [
      "#FF5733", "#33FF57", "#3366FF", "#FF33E3", "#FFD700",
      "#FF4500", "#00FFFF", "#8A2BE2", "#00FF00", "#FF1493",
      "#1E90FF", "#32CD32", "#FF6347", "#FF8C00", "#BA55D3",
      "#ADFF2F", "#20B2AA", "#FF69B4", "#DC143C", "#FFD700",
      "#FF00FF", "#00FA9A", "#FF4500", "#00CED1", "#9932CC",
      "#DA70D6", "#FFB6C1", "#FFA500", "#7FFF00", "#40E0D0"
    ];
    return colors[id % colors.length]; // Sempre retorna a mesma cor para o mesmo ID
  };

  static abbreviateName = (fullName: string, chars: number): string => {
    const firstName = fullName.split(' ')[0];
    return firstName.length > (chars + 3) ? firstName.slice(0, chars) + "..." : firstName;
  };

  static clearAndFocusField(ref: MutableRefObject<HTMLInputElement | null>) {
    if (ref && ref.current) {
      ref.current.value = "";
      ref.current.focus();
      ref.current.setSelectionRange(0, 0);
    } else {
      console.error("Referência inválida ou não encontrada.");
    }
  }

  static formatToBRMoney(value: number): string {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  static toOptions = <T,>(
    arr: T[] | undefined | null,
    getLabel: (x: T) => any,
    getValue: (x: T) => any
  ) => (Array.isArray(arr) ? arr : [])
    .map((item) => {
      const label = getLabel(item);
      const value = getValue(item);
      return {
        label: String(label ?? ""),
        value: String(value ?? "")
      };
    })
    .filter(o => o.value !== "");

  static generateUid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
}



